/**
 * Контракты очереди синхронизации.
 *
 * Очередь — чистая логика без браузерных API: её хранилище подключается
 * снаружи через порт (docs/ARCHITECTURE.md §3.3).
 */

import type { SyncId } from '@type2x/shared-contracts';

/** Отложенная операция. */
export interface QueuedOperation {
  readonly syncId: SyncId;
  /** Путь API относительно версионного префикса. */
  readonly path: string;
  readonly body: unknown;
  /** Календарная метка постановки в очередь (ISO 8601). */
  readonly queuedAt: string;
  /** Сколько раз отправка уже не удалась. */
  readonly attempts: number;
}

/**
 * Решение по одной операции.
 *
 * Три исхода вместо двух нужны потому, что «не удалось» бывает разным:
 * временная недоступность требует повтора, а отвергнутая сервером операция
 * будет отвергаться вечно и должна покинуть очередь.
 */
export type DeliveryVerdict = 'delivered' | 'retry' | 'drop';

/** Отправитель одной операции. */
export type Sender = (operation: QueuedOperation) => Promise<DeliveryVerdict>;

/** Итог попытки разобрать очередь. */
export interface FlushResult {
  readonly sent: number;
  readonly dropped: number;
  readonly remaining: number;
}

/** Хранилище очереди. */
export interface SyncQueueRepository {
  load(): Promise<QueuedOperation[]>;
  save(operations: readonly QueuedOperation[]): Promise<void>;
}

/**
 * Сколько неудачных попыток выдерживает операция.
 *
 * После этого она отбрасывается: бесконечно растущая очередь вредит больше,
 * чем потерянный агрегат одной сессии, а прогресс всё равно хранится локально.
 */
export const MAX_DELIVERY_ATTEMPTS = 10;
